import {useTranslation} from 'react-i18next';
import {useRouteMatch} from 'react-router-dom';
import {User} from '../../../../../../backend/interfaces/user';
import {translations} from '../../../../../../i18n/translations';
import {PaginatedTableData} from '../../../../../../interfaces/paginated-table-data';
import {UsersRepository} from '../../../../../../repositories/users';
import {UserType} from '../../../../../../backend/enums/user-type';
import {useAppContext} from '../../../../../../contexts/app/context';
import {Box, Stack} from '@mui/material';
import GenericHeader from '../../../../../../components/generic-header/generic-header';
import SearchInput from '../../../../../../components/search-input/search-input';
import {usePaginatedTable} from '../../../../../../hooks';
import {PaginatedTable} from '../../../../../../components/paginated-table/paginated-table';

// Columns IDs
enum ColumnIdentifier {
  Name = 'name',
  Email = 'email',
  State = 'state',
}

/**
 * Professionals and informal caregivers
 */
export default function CaregiversSystemAdministratorOfficeScreen() {
  const appContext = useAppContext();
  const {t} = useTranslation();
  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {url} = useRouteMatch();

  // Fetch caregivers
  const handleGetCaregivers = async (limit: number, skip: number, search?: string) => {
    // Load caregivers
    const result: {totalCount: number; data: Array<User>} =
      await UsersRepository.instance.getAsSystemAdministrator(
        limit,
        skip,
        UserType.Caregiver,
        appContext.state.configuration.product,
        undefined,
        search
      );

    // Process data
    const processedData: PaginatedTableData<User> = result.data.map((obj) => {
      // Return row values
      return {
        id: obj.id || '',
        item: obj,
        enabled: obj.isActive,
        avatarUrl: obj.picture,
        toLink: url.charAt(url.length - 1) === '/' ? `${url}${obj.id}` : `${url}/${obj.id}`,
        columns: [
          {
            columnId: ColumnIdentifier.Name,
            value: obj.name || '',
          },
          {
            columnId: ColumnIdentifier.Email,
            value: obj.email || '',
          },
          {
            columnId: ColumnIdentifier.State,
            value: obj.isActive
              ? t(translations.caregivers.root.state_value_true)
              : t(translations.caregivers.root.state_value_false),
          },
        ],
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    currentSearch,
    handlePageChange,
    handleUpdateCurrentSearch,
    handleApplyFilters,
  } = usePaginatedTable<User>({
    fetchData: handleGetCaregivers,
  });

  return (
    <Box display="flex" flexDirection="column" flexGrow={1}>
      <GenericHeader
        pageTitle={t(translations.screen_paths.senior_caregivers)}
        isLoading={false}
        leftColumn={
          <Stack
            gap={2}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              paddingTop: '10px',
            }}
          >
            <SearchInput
              placeholder={t(translations.caregivers.root.search_input_placeholder)}
              value={currentSearch}
              onChange={(event) => handleUpdateCurrentSearch(event.target.value)}
              onSearchAction={(value: string) => handleApplyFilters(1, value)}
            />
          </Stack>
        }
        rightColumn={null}
      />
      <Box className="listing-container">
        <Box className="listing-container-centered">
          <PaginatedTable
            avatar={{type: 'user'}}
            emptyTableMessage={t(translations.caregivers.root.no_caregivers)}
            columns={[
              {
                columnId: ColumnIdentifier.Name,
                title: t(translations.caregivers.root.name_header),
                flexGrow: 2,
              },
              {
                columnId: ColumnIdentifier.Email,
                title: t(translations.caregivers.root.email_header),
                flexGrow: 2,
              },
              {
                columnId: ColumnIdentifier.State,
                title: t(translations.caregivers.root.state_header),
                flexGrow: 1,
              },
            ]}
            rows={data}
            isLoading={isLoading}
            error={error}
            pagesCount={pagesCount}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            linkableRows={true}
          />
        </Box>
      </Box>
    </Box>
  );
}
